import React, { useState, useEffect, useRef } from "react";
import imagesSizes from "constants/imagesSizes";
import { errorService, getImage } from "app/utils";
import Subtitles from "components/common/Subtitles";
import { isMediaVideoType } from "../../utils";
import { PostStoryState } from "../../types";

interface VideoPlayerProps {
  currentStory: PostStoryState["currentStory"];
  onPauseVideo: (value?: boolean) => void;
  pausedVideo: boolean;
  postingStory: PostStoryState["postingStory"];
  selectedThumbnailUrl: PostStoryState["selectedThumbnailUrl"];
  selectedMedia: PostStoryState["selectedMedia"];
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  currentStory,
  onPauseVideo,
  pausedVideo,
  postingStory,
  selectedThumbnailUrl,
  selectedMedia
}) => {
  const [src, setSrc] = useState<string>();
  const [poster, setPoster] = useState<string>();
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current) videoRef.current.focus();
  }, []);

  useEffect(() => {
    if (videoRef.current && pausedVideo) videoRef.current.pause();
  }, [pausedVideo]);

  useEffect(() => {
    const getCachedUrl = () => {
      if (isMediaVideoType(selectedMedia as Blob)) return "";

      return "?not-from-cache-please";
    };

    if (selectedThumbnailUrl) {
      setPoster(`${selectedThumbnailUrl}${getCachedUrl()}`);
    } else {
      setPoster(
        `${selectedMedia ? URL.createObjectURL(selectedMedia as Blob) : ""}${getCachedUrl()}`
      );
    }
  }, [selectedMedia, selectedThumbnailUrl]);

  useEffect(() => {
    const link = getImage(imagesSizes.NORMAL, selectedMedia);
    if (link) {
      setSrc(link);
    } else if (isMediaVideoType(selectedMedia as Blob)) {
      setSrc(URL.createObjectURL(selectedMedia as Blob));
    } else {
      setSrc(selectedMedia ? URL.createObjectURL(selectedMedia as Blob) : "");
    }
  }, [selectedMedia]);

  const handleMetadataLoaded = (
    event: React.SyntheticEvent<HTMLVideoElement>
  ) => {
    try {
      const videoEl = event.currentTarget;
      const videoDuration = videoEl.duration;

      if (videoDuration === Infinity) {
        videoEl.currentTime = Number.MAX_SAFE_INTEGER;
        videoEl.ontimeupdate = () => {
          videoEl.ontimeupdate = null;
          videoEl.currentTime = 0.1;
        };
      }

      videoEl.play();
      videoEl.pause();
    } catch (err) {
      errorService.error(err);
    }
  };

  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video
      ref={videoRef}
      className="chq-ffd--video"
      controlsList="nodownload"
      crossOrigin="anonymous"
      controls={!postingStory}
      disablePictureInPicture
      onLoadedMetadata={handleMetadataLoaded}
      onPlay={() => onPauseVideo(false)}
      poster={poster}
      src={src}
    >
      {!isMediaVideoType(selectedMedia as Blob) && currentStory && (
        <Subtitles media={currentStory.media} />
      )}
    </video>
  );
};

export default VideoPlayer;
